if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        // An <add-on-modal> is only rendered for products that opt into the post-add
        // cross-sell, so its presence is the whole trigger condition. It refreshes the
        // drawer silently, which is why the drawer must not also be opened.
        this.addOnModal = document.querySelector('add-on-modal');
        this.cart =
          this.addOnModal ||
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');
        this.showAddToCartModal = false;
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      connectedCallback() {
        const isPDP = document.querySelector('.product__info-container')
        if (!!isPDP)
          window.waitForMod('AssemblyService').then(() => {
            window.waitForEl('.assembly-options', this).then(AssemblyService.pdp.init.bind(AssemblyService.pdp));
          });
        this.applyCrossSellTracking();
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.submitButton.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        const formData = new FormData(this.form);
        // The modal delegates its sections to the drawer, so the drawer element still has
        // to be upgraded before the request is built.
        if (this.addOnModal && !customElements.get('cart-drawer') && window.luxoLoadCartDrawer) {
          try {
            await window.luxoLoadCartDrawer();
            this.addOnModal.cartDrawer = document.querySelector('cart-drawer');
          } catch (error) {
            console.warn('Unable to load cart drawer before add to cart:', error);
          }
        }

        // Both scripts are deferred; if the modal has not upgraded yet the generic guard
        // below would discard it and fall back to a full page redirect to /cart.
        if (this.addOnModal && typeof this.addOnModal.getSectionsToRender !== 'function') {
          await customElements.whenDefined('add-on-modal').catch(() => null);
          if (typeof this.addOnModal.getSectionsToRender === 'function') this.cart = this.addOnModal;
        }

        if (this.cart && typeof this.cart.getSectionsToRender !== 'function') {
          if (window.luxoLoadCartDrawer && this.cart.matches?.('cart-drawer')) {
            try {
              this.cart = await window.luxoLoadCartDrawer();
            } catch (error) {
              console.warn('Unable to load cart drawer before add to cart:', error);
              this.cart = null;
            }
          }

          if (this.cart && typeof this.cart.getSectionsToRender !== 'function') {
            this.cart = null;
          }
        }

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        const payload = this.buildCartAddPayload(formData);
        const crossSellAddonIds = payload ? payload.crossSellAddonIds : [];
        this.applyCartAddBody(config, formData, payload);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            // /cart/add.js is atomic for multi-item payloads: if a cross-sell add-on
            // sold out between render and click, the main product is rejected too.
            // Retry with the add-ons stripped so a cross-sell never costs the primary add.
            if (response.status && crossSellAddonIds.length) {
              return this.retryWithoutCrossSellAddons(formData);
            }
            return response;
          })
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal && this.showAddToCartModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
            this.submitButton.querySelector('.loading__spinner').classList.add('hidden');
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;
        if (window.swal && errorMessage) {
          window.swal.fire({
            text: errorMessage,
            icon: 'error',
            customClass: 'swal2-error'
          })
        } else {
          this.errorMessageWrapper =
            this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
          if (!this.errorMessageWrapper) return;
          this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

          this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

          if (errorMessage) {
            this.errorMessage.textContent = errorMessage;
          }
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      collectLineItemProperties(formData) {
        const properties = {};
        for (const [key, value] of formData.entries()) {
          if (key.startsWith('properties[') && key.endsWith(']')) {
            properties[key.slice(11, -1)] = value;
          }
        }
        return properties;
      }

      /**
       * Builds the multi-item /cart/add.js payload when the customer has opted into
       * any add-on (in-home assembly and/or a cross-sell item). Returns null when there
       * are none, so the plain FormData POST is used unchanged for the common case.
       */
      buildCartAddPayload(formData) {
        const assemblyServiceId = formData.get('assembly_service');
        // getAll, not Object.fromEntries: duplicate keys would otherwise collapse to one.
        const crossSellAddonIds = formData.getAll('addon_products[]').filter(Boolean);
        if (!assemblyServiceId && !crossSellAddonIds.length) return null;

        const id = formData.get('id');
        const quantity = formData.get('quantity') || 1;
        const sections = formData.get('sections');
        const properties = this.collectLineItemProperties(formData);

        // The assembly label rides on the main line item only when assembly is selected.
        if (!assemblyServiceId) delete properties['_Assembly Service'];

        const items = [{ id, quantity, properties }];

        if (assemblyServiceId) {
          const rawAssemblyProperty = formData.get('properties[_Assembly Service]') || '';
          const separatorIndex = rawAssemblyProperty.lastIndexOf('-');
          items.push({
            id: assemblyServiceId,
            quantity: 1,
            // parent_id is a back-office/fulfilment contract - assembly stays a child line.
            parent_id: id,
            properties: {
              '': separatorIndex > -1 ? rawAssemblyProperty.substring(0, separatorIndex) : rawAssemblyProperty,
            },
          });
        }

        crossSellAddonIds.forEach((variantId) => {
          items.push({
            id: variantId,
            quantity: 1,
            // Deliberately no parent_id: the customer must be able to remove the add-on
            // without losing the product it was offered against.
            properties: {
              __cross_sell: 'pdp_addon',
              _addon_for: id,
            },
          });
        });

        return { items, sections, crossSellAddonIds };
      }

      applyCartAddBody(config, formData, payload) {
        if (payload) {
          const { crossSellAddonIds, ...body } = payload;
          config.body = JSON.stringify(body);
          return;
        }

        delete config.headers['Content-Type'];
        formData.delete('properties[_Assembly Service]');
        config.body = formData;
      }

      async retryWithoutCrossSellAddons(formData) {
        formData.delete('addon_products[]');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        this.applyCartAddBody(config, formData, this.buildCartAddPayload(formData));

        const response = await fetch(`${routes.cart_add_url}`, config).then((result) => result.json());
        if (!response.status) this.announceAddonUnavailable();
        return response;
      }

      announceAddonUnavailable() {
        const addons = this.querySelector('product-addons');
        if (addons && typeof addons.showUnavailableNotice === 'function') addons.showUnavailableNotice();
      }
      applyCrossSellTracking() {
        if (!this.classList.contains('product-form') || !this.form) return;

        const hiddenName = 'properties[__cross_sell]';
        const existing = this.form.querySelector(`input[name="${hiddenName}"]`);

        const crossSellSource = new URLSearchParams(window.location.search).get('crosssell');
        const source = {
          pdp: 'pdp',
          cart: 'cart',
          'cart-drawer': 'cart_drawer',
        }[crossSellSource] || null;

        if (source) {
          if (existing) {
            existing.value = source; // update if already present
          } else {
            const hidden = document.createElement('input');
            hidden.type = 'hidden';
            hidden.name = hiddenName;
            hidden.value = source;
            this.form.appendChild(hidden);
          }
        } else if (existing) {
          existing.remove(); // remove if no crosssell param
        }
      }
    }
  );
}
